import { DashboardConfig } from '@/utils/subscription/types'

/** A dashboard card as laid out by the home page. */
export type DashboardCard<T> = { id: string; config: DashboardConfig; component: T }

/**
 * A run of consecutive cards: either one full-width card, or a wide main column and a
 * narrow side column that stack independently (so a short card never leaves a hole next to a tall one).
 */
export type DashboardBand<T> =
  | { kind: 'full'; card: DashboardCard<T> }
  | { kind: 'columns'; main: DashboardCard<T>[]; side: DashboardCard<T>[] }

/** Width of a card on desktop, from its grid config (md, else sm, else xs). */
function desktopWidth(config: DashboardConfig): number {
  return config.col.md ?? config.col.sm ?? config.col.xs ?? 12
}

/**
 * Groups cards, in order, into bands for the desktop layout: 4-wide cards go to the side column,
 * other cards narrower than the page go to the main column, and full-width cards break the band.
 */
export function groupIntoBands<T>(cards: DashboardCard<T>[]): DashboardBand<T>[] {
  const bands: DashboardBand<T>[] = []
  let current: { kind: 'columns'; main: DashboardCard<T>[]; side: DashboardCard<T>[] } | null = null
  for (const card of cards) {
    const width = desktopWidth(card.config)
    if (width >= 12) {
      current = null
      bands.push({ kind: 'full', card })
      continue
    }
    if (!current) {
      current = { kind: 'columns', main: [], side: [] }
      bands.push(current)
    }
    ;(width <= 4 ? current.side : current.main).push(card)
  }
  return bands
}
